/**
 * DirectorVouchRequest - "Send to a director to vouch for ID", on the AML /
 * Thirdfort review card.
 *
 * BM-H4024 (Vanessa, 8 Oct 2026): "need the 'send to director to vouch for
 * ID' button here". The vouch itself (BM-H3306, DirectorVouchPanel) is
 * directors only, so a fee earner or client care had no way to hand a
 * matter to a director from the card.
 *
 * This moves no gate. It drives POST /vouching/matter/{ref}/request-vouch,
 * which records who asked and emails the directors a link to the matter.
 * Only the director's own vouch clears AML. Once sent, the card says who
 * sent it and when instead of offering the button again; the server also
 * refuses a second request while one is pending.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, UserCheck } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const DECLINED = ["referred", "rejected"];
const FIELD = "w-full text-xs bg-[rgba(13,20,36,0.8)] border border-navy-border rounded p-2 text-white";

export default function DirectorVouchRequest({ matterRef, ob, apiBase = API, onSent }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    if (!ob || ob.aml_satisfied) return null;
    if (DECLINED.includes((ob.thirdfort_report_outcome || "").toLowerCase())) return null;

    const pending = ob.director_vouch_request?.status === "pending" ? ob.director_vouch_request : null;
    if (pending) {
        return (
            <p className="mt-3 text-xs text-text-muted" data-testid="director-vouch-request-pending">
                <UserCheck className="inline w-3.5 h-3.5 mr-1 text-teal" />
                Sent to the directors to vouch for ID by {pending.requested_by} on{" "}
                {pending.requested_at ? new Date(pending.requested_at).toLocaleDateString("en-GB") : "an unknown date"}.
                {" "}A director records the vouch on this matter, and that clears the AML step.
            </p>
        );
    }

    const send = async () => {
        setBusy(true);
        try {
            const res = await axios.post(
                `${apiBase}/vouching/matter/${encodeURIComponent(matterRef)}/request-vouch`,
                { note: note.trim() },
            );
            if (res.data?.emailed) {
                toast.success(res.data.message || "Sent to the directors to vouch for ID.");
            } else {
                toast.warning(res.data?.message || "Recorded, but no email went to the directors. Tell a director directly.");
            }
            setOpen(false);
            setNote("");
            if (onSent) await onSent();
        } catch (e) {
            toast.error(errorMessage(e, "Could not send this to a director."));
            if (onSent) await onSent();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 pt-3 border-t border-navy-border" data-testid="director-vouch-request">
            {!open ? (
                <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="text-xs"
                    data-testid="director-vouch-request-btn">
                    <Send className="w-3.5 h-3.5 mr-1" /> Send to a director to vouch for ID
                </Button>
            ) : (
                <div className="space-y-2">
                    <p className="text-[11px] text-text-muted">
                        For when a Thirdfort check is not possible. The directors are emailed a link to
                        this matter. Nothing clears until a director records the vouch.
                    </p>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                        placeholder="Why Thirdfort is not possible here (optional)" className={FIELD}
                        data-testid="director-vouch-request-note" />
                    <div className="flex gap-2">
                        <Button size="sm" onClick={send} disabled={busy} className="text-xs"
                            data-testid="director-vouch-request-send">
                            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Send to directors
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="text-xs">Cancel</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
